import React, { useEffect, useRef } from 'react';

import ProjectsPaywall from '@components/ProjectsPaywall';
import useEventTracking from '@hooks/useEventTracking';
import useIframeResizeMessager from '@hooks/useIframeResizeMessager';
import { HTMLViaNotesProps } from '@interfaces/projects';
import { project_path } from '@routes';
import { ClickTarget, Page, click } from '@tracking';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import TopSection from './TopSection';

const ANCHOR_TAG = 'a';

const HTMLViaNotes = ({
  data,
  paywallData,
  project,
  projectMedia,
  unlocked,
}: HTMLViaNotesProps): JSX.Element => {
  const projectPageRef = useRef<HTMLDivElement>(null);
  useIframeResizeMessager();
  const projectImagePath = project_path(project.slug);
  const { Track, trackEvent } = useEventTracking({
    location: Page.Project,
    contentId: project.id,
  });

  useEffect(() => {
    const projectElement = projectPageRef.current;

    if (!projectElement || !trackEvent) return;

    function handleProjectClick(this: HTMLElement, event: MouseEvent) {
      const link = (event.target as HTMLElement).closest(ANCHOR_TAG);
      if (link) {
        trackEvent(
          click({
            clickTarget: ClickTarget.AnchorLink,
            clickLink: link.href,
            clickText: link.innerText,
          })
        );
      }
    }

    projectElement.addEventListener('click', handleProjectClick, { capture: true, passive: true });

    return () => {
      projectElement.removeEventListener('click', handleProjectClick, {
        capture: true,
        passive: true,
      } as EventListenerOptions);
    };
  }, [trackEvent]);

  return (
    <Track>
      <main className="w-full px-16 sm:px-28" ref={projectPageRef}>
        <TopSection
          accessLevel={project.accessLevel}
          authors={project.authors}
          description={project.description}
          mediaSourcePath={projectImagePath}
          projectMedia={projectMedia}
          title={project.title}
          titleCallout={project.titleCallout}
          updatedAt={project.updatedAt}
        />
        {!unlocked ? (
          <>
            <div className="relative before:absolute before:bottom-0 before:left-0 before:z-10 before:h-[300px] before:w-full before:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] before:content-[''] dark:before:bg-[linear-gradient(to_top,#1a1a1a_0%,rgba(26,26,26,0)_100%)]"></div>
            <div className="sticky bottom-0 z-50">
              <ProjectsPaywall {...paywallData} leadType={project.leadType} />
            </div>
          </>
        ) : (
          <div className="mx-auto mt-32 w-full max-w-container-lg sm:mt-48">
            <span id="no-paywall"></span>
            <div className="mx-auto w-full">{data && AuthoredContentReactToHtmlParser(data)}</div>
          </div>
        )}
      </main>
    </Track>
  );
};

export default HTMLViaNotes;
